import { KeyValueRow } from '@/components/Common/KeyValueRow';
import type { CartItemProduct, CartLineItem, Pricing } from '@/types/checkout';
import { formatPriceWithCurrency } from '@/utils/pricing';
import {
  getCartLineItemAssetUrl,
  getCartLineItemDisplayName,
  getCartLineItemQuantityLabel,
  shouldShowCartLineItemQuantityBadge,
} from '@/utils/offers';

const formatProductQuantity = (product: CartItemProduct): string => product.quantityDisplay ?? String(product.quantity);

export const OfferListItem = ({ lineItem, pricing }: { lineItem: CartLineItem; pricing: Pricing }) => {
  const products = lineItem.offer.products ?? [];
  const showQuantityBadge = shouldShowCartLineItemQuantityBadge(lineItem);
  const displayName = getCartLineItemDisplayName(lineItem);

  return (
    <div
      data-testid="offers-list-item"
      className="relative flex w-full gap-2 border-b border-[#dedede] pb-[18px] last:border-b-0 last:pb-0"
    >
      <div className="size-10 shrink-0 overflow-hidden rounded-[4px] border border-current bg-white">
        <img src={getCartLineItemAssetUrl(lineItem)} alt={displayName} className="size-full object-contain" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <KeyValueRow
          align="start"
          labelLines={2}
          label={displayName}
          value={<span data-testid="offer-line-item-price">{formatPriceWithCurrency(lineItem.price, pricing)}</span>}
          labelClassName="text-sm font-semibold leading-5"
          valueClassName="text-sm font-semibold leading-4"
        />
        {products.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="offer-product-rows">
            {products.map((product) => (
              <KeyValueRow
                key={product.sku}
                label={product.displayName ?? product.name}
                value={formatProductQuantity(product)}
                labelClassName="text-xs opacity-70"
                valueClassName="text-xs opacity-70"
              />
            ))}
          </div>
        )}
      </div>
      {showQuantityBadge && (
        <div className="pointer-events-none absolute left-[23px] top-[30px] flex h-3.5 min-w-[21px] items-center justify-center rounded-full bg-black backdrop-blur-sm">
          <span className="text-[8px] font-semibold leading-[11px] text-white" data-testid="offer-quantity-badge">
            {getCartLineItemQuantityLabel(lineItem)}
          </span>
        </div>
      )}
    </div>
  );
};
